import { useSortable } from '@dnd-kit/sortable';
import { Badge, Box, HStack, IconButton, Td, Text, Tooltip, Tr } from '@chakra-ui/react';
import { FiCopy, FiSettings, FiTrash2 } from 'react-icons/fi';
import { MdDragHandle } from 'react-icons/md';
import { TaskType } from '@/lib/sdk/chatbot/types';
import { GridRow, GridAction } from './useTaskGridReducer';
import { TextCell, TextAreaCell, SelectCell } from './GridCells';
import { DATA_PLACEHOLDER, PANEL_ONLY_TYPES } from './taskGridConstants';

interface FillState {
  srcRowIdx: number;
  col: string;
  value: string;
  srcType: TaskType;
  endRow: number;
}

interface SortableTaskRowProps {
  row: GridRow;
  rowIdx: number;
  dispatch: React.Dispatch<GridAction>;
  template: string;
  showDataCol: boolean;
  typeOptions: { value: TaskType; label: string }[];
  isFillTarget: (rowIdx: number, col: string) => boolean;
  fillTargetShadow: string;
  fillState: FillState | null;
  handleKeyDown: (e: React.KeyboardEvent<HTMLElement>, rowIdx: number, col: string) => void;
  startFill: (rowIdx: number, col: string, value: string, srcType: TaskType) => void;
  updateFillEndRow: (rowIdx: number) => void;
  onOpenConfigPanel?: (row: GridRow, rowIdx: number) => void;
  onOpenSubtaskEditor?: (row: GridRow, rowIdx: number) => void;
  /** The AI assistant changed this task in the draft now open in the editor */
  isHighlighted?: boolean;
}

export function SortableTaskRow({
  row,
  rowIdx,
  dispatch,
  template,
  showDataCol,
  typeOptions,
  isFillTarget,
  fillTargetShadow,
  fillState,
  handleKeyDown,
  startFill,
  updateFillEndRow,
  onOpenConfigPanel,
  onOpenSubtaskEditor,
  isHighlighted = false,
}: SortableTaskRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row._localId,
  });

  const hasError = Object.values(row._errors ?? {}).some(Boolean);
  const dataPlaceholder = DATA_PLACEHOLDER[row.type] ?? '';
  const isDataRelevant =
    !PANEL_ONLY_TYPES.has(row.type) &&
    row.type !== TaskType.REGULAR &&
    row.type !== TaskType.END &&
    row.type !== TaskType.MEDIA &&
    row.type !== TaskType.PHOTO &&
    row.type !== TaskType.VIDEO &&
    row.type !== TaskType.DOCUMENT &&
    row.type !== TaskType.DATE &&
    row.type !== TaskType.NUMBER &&
    row.type !== TaskType.TEXT &&
    row.type !== TaskType.AUDIO;
  const isPanelOnly = PANEL_ONLY_TYPES.has(row.type);

  return (
    <Tr
      ref={setNodeRef}
      data-testid={`wfv4-grid-row-${rowIdx}`}
      style={{
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        transition,
        opacity: isDragging ? 0.4 : 1,
      }}
      data-ai-changed={isHighlighted ? 'true' : undefined}
      borderLeftWidth={hasError || isHighlighted ? '3px' : '0'}
      borderLeftColor={hasError ? 'red.400' : isHighlighted ? 'purple.400' : 'transparent'}
      _hover={{ bg: 'interactive-hover' }}
      onMouseEnter={() => { if (fillState) updateFillEndRow(rowIdx); }}
    >
      {/* Drag handle */}
      <Td px={1} borderColor="border-default" w="36px" {...attributes}>
        <Box
          as="span"
          display="flex"
          alignItems="center"
          justifyContent="center"
          color="text-secondary"
          cursor="grab"
          sx={{ '&:active': { cursor: 'grabbing' } }}
          data-testid={`wfv4-grid-drag-${rowIdx}`}
          {...listeners}
        >
          <MdDragHandle size={16} />
        </Box>
      </Td>

      {/* Name */}
      <Td p={0} borderColor="border-default" minW="200px" boxShadow={isFillTarget(rowIdx, 'name') ? fillTargetShadow : undefined}>
        <TextCell
          id={`wt-cell-${rowIdx}-name`}
          testId={`wfv4-grid-name-${rowIdx}`}
          value={row.name}
          onChange={(v) => dispatch({ type: 'SET_CELL', localId: row._localId, col: 'name', value: v })}
          onKeyDown={(e) => handleKeyDown(e, rowIdx, 'name')}
          error={row._errors?.['name']}
          onFillStart={() => startFill(rowIdx, 'name', row.name, row.type)}
          placeholder="Task name"
          maxLength={24}
        />
      </Td>

      {/* Description */}
      <Td p={0} borderColor="border-default" minW="240px" boxShadow={isFillTarget(rowIdx, 'description') ? fillTargetShadow : undefined}>
        <TextAreaCell
          id={`wt-cell-${rowIdx}-description`}
          testId={`wfv4-grid-description-${rowIdx}`}
          value={row.description}
          onChange={(v) => dispatch({ type: 'SET_CELL', localId: row._localId, col: 'description', value: v })}
          onKeyDown={(e) => handleKeyDown(e, rowIdx, 'description')}
          error={row._errors?.['description']}
          onFillStart={() => startFill(rowIdx, 'description', row.description, row.type)}
          placeholder="Optional description"
        />
      </Td>

      {/* Type */}
      <Td p={0} borderColor="border-default" minW="150px" boxShadow={isFillTarget(rowIdx, 'type') ? fillTargetShadow : undefined}>
        <SelectCell
          id={`wt-cell-${rowIdx}-type`}
          testId={`wfv4-grid-type-${rowIdx}`}
          value={row.type}
          onChange={(v) => dispatch({ type: 'SET_CELL', localId: row._localId, col: 'type', value: v })}
          onKeyDown={(e) => handleKeyDown(e, rowIdx, 'type')}
          options={typeOptions}
          error={row._errors?.['type']}
          onFillStart={() => startFill(rowIdx, 'type', row.type, row.type)}
        />
      </Td>

      {/* Data */}
      {showDataCol && (
        <Td
          p={0}
          borderColor="border-default"
          minW="160px"
          boxShadow={isFillTarget(rowIdx, 'data') ? fillTargetShadow : undefined}
          opacity={isDataRelevant ? 1 : 0.4}
        >
          {isPanelOnly ? (
            <Box
              px={2}
              py={1}
              h="32px"
              display="flex"
              alignItems="center"
              borderWidth="1px"
              borderColor={row._errors?.['data'] ? 'red.400' : 'border-default'}
              cursor="not-allowed"
            >
              <Tooltip label={row._errors?.['data']} isDisabled={!row._errors?.['data']} placement="top" hasArrow bg="red.500">
                <Text fontSize="xs" color={row._errors?.['data'] ? 'red.400' : 'text-muted'} noOfLines={1}>
                  {dataPlaceholder}
                </Text>
              </Tooltip>
            </Box>
          ) : (
            <TextCell
              id={`wt-cell-${rowIdx}-data`}
              testId={`wfv4-grid-data-${rowIdx}`}
              value={row.data}
              onChange={(v) => dispatch({ type: 'SET_CELL', localId: row._localId, col: 'data', value: v })}
              onKeyDown={(e) => handleKeyDown(e, rowIdx, 'data')}
              error={row._errors?.['data']}
              onFillStart={isDataRelevant ? () => startFill(rowIdx, 'data', row.data, row.type) : undefined}
              placeholder={dataPlaceholder}
            />
          )}
        </Td>
      )}

      {/* Group */}
      <Td p={0} borderColor="border-default" minW="120px" boxShadow={isFillTarget(rowIdx, 'group') ? fillTargetShadow : undefined}>
        <TextCell
          id={`wt-cell-${rowIdx}-group`}
          testId={`wfv4-grid-group-${rowIdx}`}
          value={row.group}
          onChange={(v) => dispatch({ type: 'SET_CELL', localId: row._localId, col: 'group', value: v })}
          onKeyDown={(e) => handleKeyDown(e, rowIdx, 'group')}
          onFillStart={() => startFill(rowIdx, 'group', row.group, row.type)}
          placeholder="Group name"
        />
      </Td>

      {/* Configure */}
      <Td px={1} borderColor="border-default" w="110px">
        <HStack spacing={1}>
          <Tooltip label="Configure advanced options">
            <IconButton
              aria-label="Configure task"
              icon={<FiSettings />}
              size="xs"
              variant="ghost"
              colorScheme="brand"
              data-testid={`wfv4-grid-configure-btn-${rowIdx}`}
              onClick={() => {
                if (row.type === TaskType.TASKLIST) {
                  onOpenSubtaskEditor?.(row, rowIdx);
                } else {
                  onOpenConfigPanel?.(row, rowIdx);
                }
              }}
            />
          </Tooltip>
          {row._hasAdvanced && (
            <Badge colorScheme="brand" fontSize="xs" variant="subtle">
              {row.actions.length > 0 ? `${row.actions.length}` : '●'}
            </Badge>
          )}
        </HStack>
      </Td>

      {/* Duplicate / Delete */}
      <Td px={1} borderColor="border-default" w="68px">
        <HStack spacing={0}>
          <IconButton
            aria-label="Duplicate row"
            icon={<FiCopy />}
            size="xs"
            variant="ghost"
            colorScheme="brand"
            data-testid={`wfv4-grid-duplicate-btn-${rowIdx}`}
            onClick={() => dispatch({ type: 'DUPLICATE_ROW', localId: row._localId })}
          />
          <IconButton
            aria-label="Delete row"
            icon={<FiTrash2 />}
            size="xs"
            variant="ghost"
            colorScheme="red"
            data-testid={`wfv4-grid-delete-btn-${rowIdx}`}
            onClick={() => dispatch({ type: 'DELETE_ROW', localId: row._localId })}
          />
        </HStack>
      </Td>
    </Tr>
  );
}
